import { ColDef, ColGroupDef, ColumnState } from "ag-grid-community";
import { EQM2050_R01Dto } from "../Dto_EQM2050_R01";
import { getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { aggridHeaderClass } from "src/components/common/common";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { formatGridNumber3 } from "src/utils/number/numberUtils";
import { formatDate } from "src/utils/string/stringUtil";
import {
	getDayOfWeekIndexFromDateString,
	getDayNameFromDateString,
} from "src/utils/date/dateUtils";

/**
 * 헤더 이름 매핑
 */
export const headerNames: Record<string, string> = {
	rowIndex: "",
	inspectionDt: "일자",
	dayOfWeek: "요일",
	industrialWater: '산업용수',
	domesticWater: '생활용수',
	electricalEnergy01: '옥상1',
	electricalEnergy02: '옥상2',
	electricalEnergy03: '옥상3',
	remark: '비고'
};

/**
 * 기본 컬럼 정의 생성 함수
 */
export const createBaseColumnDefs = (
	cellDateComparator: (valueA: Date, valueB: Date) => number
): (ColDef<EQM2050_R01Dto> | ColGroupDef<EQM2050_R01Dto>)[] => {
	return [
		// ...getDefaultRowIndexColumnConfig(renderIndexCell),
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '일자',
			colId: 'inspectionDt',
			field: 'inspectionDt',
			width: 100,
			valueFormatter: (params: any) => {
				if (!params.data?.inspectionDt) return '';
				return formatDate(new Date(params.data.inspectionDt));
			},
			comparator: cellDateComparator,
			editable: false,
		},
		{
			headerName: '요일',
			colId: 'dayOfWeek',
			width: 50,
			valueGetter: (params: any) => getDayNameFromDateString(params.data?.inspectionDt),
			cellClassRules: {
				'eqm2050-sunday-cell': (params: any) => getDayOfWeekIndexFromDateString(params.data?.inspectionDt) === 0,
				'eqm2050-saturday-cell': (params: any) => getDayOfWeekIndexFromDateString(params.data?.inspectionDt) === 6,
				'text-align-center': (params: any) => true,
			},
			editable: false,
		},
		{
			headerName: '용수사용량 지침(m³)',
			headerClass: 'common-group-header',
			children: [
				{
					headerName: '공업용수',
					colId: 'industrialWater',
					field: 'industrialWater',
					width: 100,
					valueFormatter: formatGridNumber3,
					type: 'numericColumn',
					editable: true,
				},
				{
					headerName: '생활용수',
					colId: 'domesticWater',
					field: 'domesticWater',
					width: 100,
					valueFormatter: formatGridNumber3,
					type: 'numericColumn',
					editable: true,
				},
			],
		},
		{
			headerName: '전력량(kW/h)',
			headerClass: 'common-group-header',
			children: [
				{
					headerName: '옥상1',
					colId: 'electricalEnergy01',
					field: 'electricalEnergy01',
					width: 100,
					valueFormatter: formatGridNumber3,
					type: 'numericColumn',
					editable: true,
				},
				{
					headerName: '옥상2',
					colId: 'electricalEnergy02',
					field: 'electricalEnergy02',
					width: 100,
					valueFormatter: formatGridNumber3,
					type: 'numericColumn',
					editable: true,
				},
				{
					headerName: '옥상3',
					colId: 'electricalEnergy03',
					field: 'electricalEnergy03',
					width: 100,
					valueFormatter: formatGridNumber3,
					type: 'numericColumn',
					editable: true,
				},
			],
		},
		{
			headerName: '비고',
			colId: 'remark',
			field: 'remark',
			width: 200,
			editable: true,
		},
	];
};

/**
 * 그룹 헤더 구조를 유지하면서 헤더 클래스 정규화
 */
const normalizeHeaderClass = (
	defs: (ColDef<EQM2050_R01Dto> | ColGroupDef<EQM2050_R01Dto>)[]
): (ColDef<EQM2050_R01Dto> | ColGroupDef<EQM2050_R01Dto>)[] => {
	return defs.map((def: any) => {
		// children이 있으면 재귀적으로 처리
		const children = Array.isArray(def.children) && def.children.length > 0
			? normalizeHeaderClass(def.children)
			: undefined;

		if (children) {
			// ColGroupDef인 경우 - 공통 그룹 헤더 클래스만 유지
			const groupHeaderClass = [def.headerClass, 'common-group-header']
				.filter(Boolean)
				.join(' ');
			return {
				...def,
				headerClass: groupHeaderClass,
				suppressMenu: true,
				children
			} as ColGroupDef<EQM2050_R01Dto>;
		} else {
			// ColDef인 경우 - 일반 헤더는 aggridHeaderClass 적용
			const mergedHeaderClass = [aggridHeaderClass, def.headerClass]
				.filter(Boolean)
				.join(' ');
			return {
				...def,
				headerClass: mergedHeaderClass,
				suppressMenu: true
			} as ColDef<EQM2050_R01Dto>;
		}
	}).filter(Boolean) as (ColDef<EQM2050_R01Dto> | ColGroupDef<EQM2050_R01Dto>)[];
};

/**
 * 최종 컬럼 정의 생성 (컬럼 상태 병합 포함)
 */
export const createColumnDefs = (
	baseColumnDefs: (ColDef<EQM2050_R01Dto> | ColGroupDef<EQM2050_R01Dto>)[],
	columnState: ColumnState[] | null
): (ColDef<EQM2050_R01Dto> | ColGroupDef<EQM2050_R01Dto>)[] => {
	// analysisDt 컬럼을 제거한 baseColumnDefs 사용
	// analysisDt 컬럼은 이미 baseColumnDefs에 포함되어 있으므로 필터링 불필요
	const filteredBaseColumnDefs = baseColumnDefs;

	const mergedColumnDefs = GridCustomColumnDefs<EQM2050_R01Dto>({
		baseColumnDefs: filteredBaseColumnDefs,
		columnState: columnState || null,
		headerNames,
		fallbackHeaderClass: aggridHeaderClass
	});

	return normalizeHeaderClass(mergedColumnDefs);
};

